import { useRouter } from 'next/router';
import { useMemo } from 'react';
import type { SignPageCode } from './page';
import type { SignTabCode } from './tab';

const isSignTabCode = (() => {
  const signTabCodes = new Set<SignTabCode>([
    'find',
    'in',
    'up',
    'alreadyuser',
    'notuser',
  ]) as Set<string>;

  return (value?: string | string[] | null): value is SignTabCode => typeof value === 'string' && signTabCodes.has(value);
})();

const isSignPageCode = (() => {
  const signPageCodes = new Set<SignPageCode>([
    'resetpassword',
    'resetpasswordconfirm',
    'organization',
  ]) as Set<string>;

  return (value?: string | string[] | null): value is SignPageCode => typeof value === 'string' && signPageCodes.has(value);
})();

const isValidPageCode = (pageQuery?: string | string[]): pageQuery is SignTabCode | SignPageCode => (
  typeof pageQuery === 'string' && (
    isSignTabCode(pageQuery) || isSignPageCode(pageQuery)
  )
);

export const useSignPageControl = () => {
  const router = useRouter();

  const page = router.query.page;

  const _isSignTabCode = useMemo(() => isSignTabCode(page), [page]);
  const _isSignPageCode = useMemo(() => isSignPageCode(page), [page]);

  const handleWrongPage = () => {
    router.replace({
      query: {
        ...router.query,
        page: 'in',
      },
    });
  };

  const push = (page: SignTabCode | SignPageCode) => {
    isValidPageCode(page)
      ? router.push({
        query: {
          ...router.query,
          page,
        },
      })
      : handleWrongPage();
  };

  return {
    push,
    page: isValidPageCode(page) ? page : 'in',
    get tabCodePage() {
      if (_isSignTabCode) return page as SignTabCode;
      else return null;
    },
    get pageCodePage() {
      if (_isSignPageCode) return page as SignPageCode;
      else return null;
    },
  };
};
